import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Container,
  Grid,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { subscribeListRefresh } from "../../context/tabContext";
import PageListHeader from "../../components/pageListHeader";
import OpeningBalanceView from "../../components/accounting/OpeningBalanceView";

const emptyLine = () => ({
  accountCode: "",
  accountName: "",
  debit: 0,
  credit: 0,
  description: "",
});

const buildDefaultLines = (accountsList = []) => {
  const postable = accountsList
    .filter((account) => account.isActive !== false && !account.isControlAccount)
    .sort((a, b) =>
      String(a.code || "").localeCompare(String(b.code || ""), undefined, {
        numeric: true,
      })
    );

  if (postable.length === 0) {
    return [emptyLine(), emptyLine()];
  }

  return postable.map((account) => ({
    accountCode: account.code,
    accountName: account.name,
    debit: 0,
    credit: 0,
    description: "",
  }));
};

const formatAmount = (value) => Number(value || 0).toFixed(2);

const OpeningBalance = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const tabNav = useTabNavigate();
  const location = useLocation();
  const [entryDate, setEntryDate] = useState(new Date());
  const [remark, setRemark] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    setRemark(t("accounting.openingBalance.defaultRemark"));
  }, [t]);

  const loadAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const data = await api.Accounting.getChartOfAccountsTree();
      const accountsList = Array.isArray(data) ? data : data?.accounts || [];
      setAccounts(accountsList);
      setLines(buildDefaultLines(accountsList));
      setAccountsLoaded(true);
    } catch (error) {
      showNotification({
        color: "red",
        title: t("accounting.openingBalance.loadFailed"),
        message: error?.message || String(error),
      });
    } finally {
      setAccountsLoading(false);
    }
  }, [api, t]);

  useEffect(() => {
    loadAccounts();
    return subscribeListRefresh(location.pathname, loadAccounts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const accountOptions = useMemo(
    () =>
      accounts
        .filter((account) => account.isActive !== false && !account.isControlAccount)
        .map((account) => ({
          value: account.code,
          label: `${account.code} - ${account.name}`,
          accountId: account._id,
          accountName: account.name,
        })),
    [accounts]
  );

  const linesWithIndex = useMemo(
    () =>
      lines.map((line, index) => ({
        ...line,
        _rowIndex: index,
      })),
    [lines]
  );

  const updateLine = useCallback((index, field, value) => {
    setLines((current) =>
      current.map((line, i) => (i === index ? { ...line, [field]: value } : line))
    );
  }, []);

  const addLine = useCallback(() => {
    setLines((current) => [...current, emptyLine()]);
  }, []);

  const removeLine = useCallback((index) => {
    setLines((current) => {
      if (current.length <= 2) return current;
      return current.filter((_, i) => i !== index);
    });
  }, []);

  const resetLines = useCallback(() => {
    setLines(buildDefaultLines(accounts));
  }, [accounts]);

  const totalDebit = lines.reduce((sum, line) => sum + Number(line.debit || 0), 0);
  const totalCredit = lines.reduce((sum, line) => sum + Number(line.credit || 0), 0);
  const isBalanced =
    Number(totalDebit.toFixed(2)) === Number(totalCredit.toFixed(2)) &&
    totalDebit > 0;

  const validLines = lines.filter(
    (line) => line.accountCode && (Number(line.debit) || Number(line.credit))
  );
  const canPost =
    validLines.length >= 2 && isBalanced && !loading && !accountsLoading && accountsLoaded;

  const resetForm = useCallback(() => {
    setEntryDate(new Date());
    setRemark(t("accounting.openingBalance.defaultRemark"));
    setLines(buildDefaultLines(accounts));
  }, [accounts, t]);

  const handleSubmit = useCallback(async () => {
    if (validLines.length < 2) {
      showNotification({
        color: "red",
        title: t("accounting.openingBalance.postFailed"),
        message: t("accounting.journal.linesRequired"),
      });
      return;
    }

    if (!isBalanced) {
      showNotification({
        color: "red",
        title: t("accounting.openingBalance.postFailed"),
        message: t("accounting.journal.balancedRequired"),
      });
      return;
    }

    setLoading(true);
    try {
      const payloadLines = validLines.map((line) => {
        const account = accountOptions.find((item) => item.value === line.accountCode);
        return {
          account: account.accountId,
          accountCode: line.accountCode,
          accountName: account.accountName,
          debit: Number(line.debit || 0),
          credit: Number(line.credit || 0),
          description: line.description || t("accounting.openingBalance.lineDescription"),
        };
      });

      await api.Accounting.postOpeningBalance({
        entryDate,
        remark,
        lines: payloadLines,
      });

      showNotification({
        color: "green",
        title: t("accounting.openingBalance.postSuccessTitle"),
        message: t("accounting.openingBalance.postSuccessMessage"),
      });
      setConfirmOpen(false);
      resetForm();
    } catch (error) {
      showNotification({
        color: "red",
        title: t("accounting.openingBalance.postFailed"),
        message: error?.message || String(error),
      });
    } finally {
      setLoading(false);
    }
  }, [
    accountOptions,
    api,
    entryDate,
    isBalanced,
    remark,
    resetForm,
    t,
    validLines,
  ]);

  const actionButtons = useMemo(
    () => [
      {
        label: t("accounting.openingBalance.postButton"),
        variant: "filled",
        color: "cyan",
        loading,
        disabled: !canPost,
        onClick: () => setConfirmOpen(true),
      },
      {
        label: t("accounting.journal.addLine"),
        variant: "default",
        disabled: accountsLoading,
        onClick: addLine,
      },
      {
        label: t("accounting.openingBalance.resetLines"),
        variant: "default",
        disabled: accountsLoading || accounts.length === 0,
        onClick: resetLines,
      },
      {
        label: t("Refresh"),
        variant: "default",
        loading: accountsLoading,
        onClick: loadAccounts,
      },
      {
        label: t("Close"),
        variant: "default",
        onClick: () => tabNav.closeActive(),
      },
    ],
    [
      accounts.length,
      accountsLoading,
      addLine,
      canPost,
      loadAccounts,
      loading,
      resetLines,
      t,
      tabNav,
    ]
  );

  return (
    <Container size="xl">
      <PageListHeader
        title={t("accounting.modules.openingBalance.label")}
        actionButtons={actionButtons}
      />
      <Text size="sm" color="dimmed" mb="md">
        {t("accounting.openingBalance.pageIntro")}
      </Text>

      <Paper withBorder p="md" mb="md">
        <Text size="xs" color="dimmed" mb="sm" transform="uppercase" weight={600}>
          {t("accounting.openingBalance.headerSection")}
        </Text>
        <Stack spacing="sm">
          <DatePicker
            size="sm"
            label={t("accounting.openingBalance.entryDate")}
            value={entryDate}
            onChange={setEntryDate}
            allowFreeInput
            style={{ maxWidth: 320 }}
          />
          <Textarea
            size="sm"
            label={t("accounting.journal.remark")}
            value={remark}
            onChange={(e) => setRemark(e.currentTarget.value)}
            minRows={2}
          />
        </Stack>
      </Paper>

      <Paper withBorder p="md" mb="md">
        <Text size="xs" color="dimmed" mb="sm" transform="uppercase" weight={600}>
          {t("accounting.openingBalance.totalsSection")}
        </Text>
        <Grid>
          <Grid.Col span={4}>
            <Text size="xs" color="dimmed">
              {t("Debit")}
            </Text>
            <Text weight={700} size="lg">
              {formatAmount(totalDebit)}
            </Text>
          </Grid.Col>
          <Grid.Col span={4}>
            <Text size="xs" color="dimmed">
              {t("Credit")}
            </Text>
            <Text weight={700} size="lg">
              {formatAmount(totalCredit)}
            </Text>
          </Grid.Col>
          <Grid.Col span={4}>
            <Text size="xs" color="dimmed">
              {t("Status")}
            </Text>
            <Badge mt={4} size="lg" color={isBalanced ? "green" : "red"}>
              {isBalanced
                ? t("accounting.trialBalance.balanced")
                : t("accounting.trialBalance.outOfBalance")}
            </Badge>
          </Grid.Col>
        </Grid>
        <Text size="xs" color="dimmed" mt="sm">
          {t("accounting.openingBalance.balanceHint")}
        </Text>
      </Paper>

      {accounts.length === 0 && accountsLoaded && !accountsLoading && (
        <Alert color="yellow" mb="md">
          {t("accounting.journal.noAccounts")}
        </Alert>
      )}

      <OpeningBalanceView
        lines={linesWithIndex}
        accountOptions={accountOptions}
        loading={accountsLoading}
        onUpdateLine={updateLine}
        onRemoveLine={removeLine}
      />

      <Modal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t("accounting.openingBalance.confirmTitle")}
        size="sm"
      >
        <Text size="sm" mb="md">
          {t("accounting.openingBalance.confirmMessage")}
        </Text>
        <Stack spacing="xs" mb="lg">
          <Text size="sm">
            {t("accounting.openingBalance.confirmLines", {
              count: validLines.length,
            })}
          </Text>
          <Text size="sm">
            {t("accounting.journal.totals", {
              debit: formatAmount(totalDebit),
              credit: formatAmount(totalCredit),
            })}
          </Text>
        </Stack>
        <Group position="right">
          <Button variant="default" onClick={() => setConfirmOpen(false)}>
            {t("Cancel")}
          </Button>
          <Button color="cyan" loading={loading} onClick={handleSubmit}>
            {t("accounting.openingBalance.confirmButton")}
          </Button>
        </Group>
      </Modal>
    </Container>
  );
};

export default OpeningBalance;
